/**
 * Shared side effects for job stream status updates (per workflow).
 * Used by MultiProcessingModalManager hidden streams and coordinated with ProcessingModal queue handling.
 */

import {
  setCurrentlyProcessingSession,
  updateWorkflowSessionState,
  resetDocumentsState,
} from '../store/reducers';
import { clearWorkflowFiles } from './workflowFileCache';
import { parseProcessingErrorMessage } from './processingErrorUtils';
import { clearWorkflowLastJobId } from './workflowSessionStorage';

const processNextInQueueSentForSession = new Set();

export function resetProcessNextInQueueDispatchFlag(workflowSessionId) {
  if (workflowSessionId) {
    processNextInQueueSentForSession.delete(workflowSessionId);
  }
}

/**
 * @returns {boolean} true if this call dispatched the event (first time for this session since last reset)
 */
export function tryDispatchProcessNextInQueue(workflowSessionId) {
  if (!workflowSessionId) return false;
  if (processNextInQueueSentForSession.has(workflowSessionId)) return false;
  processNextInQueueSentForSession.add(workflowSessionId);

  if (process.env.NODE_ENV === 'development') {
    try {
      const storeModule = require('../store/store');
      const store = storeModule.default || storeModule;
      const queue = store.getState()?.workflowSessions?.processingQueue || [];
      console.log(`📤 [Workflow:${workflowSessionId}] processNextInQueue`, {
        queueLength: queue.length,
        queue,
      });
    } catch (e) {
      // ignore
    }
  }

  window.dispatchEvent(
    new CustomEvent('processNextInQueue', {
      detail: { completedSessionId: workflowSessionId },
    })
  );
  return true;
}

/**
 * Apply JobStreamDisplay status updates to workflow session + completion side effects.
 * Mirrors ProcessingModal.handleStatusUpdate completion behavior for minimized / external-stream flows.
 */
export function applyJobStreamSessionStatusUpdate(workflowSessionId, dispatch, statusUpdate) {
  if (!workflowSessionId || !dispatch || !statusUpdate) return;

  const stateUpdates = {};
  let shouldClearProcessing = false;
  let shouldSetCompleted = false;

  if (statusUpdate.areStepsCompleted !== undefined && statusUpdate.areStepsCompleted) {
    shouldSetCompleted = true;
    shouldClearProcessing = true;
  }

  if (statusUpdate.processingStatus !== undefined) {
    const processingStatus = statusUpdate.processingStatus;
    if (processingStatus === 'error') {
      stateUpdates.isProcessing = false;
      stateUpdates.processingError = parseProcessingErrorMessage(
        statusUpdate.error || 'Document processing failed'
      );
      stateUpdates.uploadSectionCollapsed = false;
      clearWorkflowLastJobId(workflowSessionId);
    } else if (processingStatus === 'completed') {
      shouldSetCompleted = true;
      shouldClearProcessing = true;
    } else if (processingStatus === 'processing') {
      stateUpdates.isProcessing = true;
    }
  }

  if (statusUpdate.currentStep !== undefined) {
    stateUpdates.currentStep = statusUpdate.currentStep;
  }
  if (statusUpdate.completedSteps !== undefined) {
    stateUpdates.completedSteps = statusUpdate.completedSteps;
  }

  if (shouldSetCompleted) {
    stateUpdates.stepsCompleted = true;
    stateUpdates.isProcessing = false;
    stateUpdates.processingError = null;
  } else if (shouldClearProcessing && stateUpdates.isProcessing === undefined) {
    stateUpdates.isProcessing = false;
  }

  if (Object.keys(stateUpdates).length > 0) {
    dispatch(updateWorkflowSessionState(workflowSessionId, stateUpdates));
  }

  if (shouldClearProcessing) {
    dispatch(setCurrentlyProcessingSession(null));
  }

  if (shouldSetCompleted) {
    clearWorkflowFiles(workflowSessionId);
    try {
      const storeModule = require('../store/store');
      const store = storeModule.default || storeModule;
      const activeId = store.getState()?.workflowSessions?.activeSessionId;
      if (activeId === workflowSessionId) {
        dispatch(resetDocumentsState());
      }
    } catch (e) {
      // store unavailable — skip resetDocumentsState
    }
    tryDispatchProcessNextInQueue(workflowSessionId);
  }
}
